import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useRates } from "@/context/RateContext";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Sparkles, Clock, ArrowRight, Gift, Sun } from "lucide-react";
import { toast } from "sonner";
import ZReportModal from "@/components/ZReportModal";

const fmt4 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function KPI({ label, value, subtext, testid }) {
  return (
    <div className="card-flat p-4 sm:p-6" data-testid={testid}>
      <div className="label-caps">{label}</div>
      <div className="font-mono text-xl sm:text-2xl lg:text-3xl mt-3 tabular-nums leading-tight break-all" style={{ color: "var(--gold)" }}>{value}</div>
      {subtext && <div className="text-xs text-neutral-500 mt-2 truncate">{subtext}</div>}
    </div>
  );
}

function TrialCountdownHero({ daysLeft, endAt }) {
  const urgent = daysLeft <= 7;
  const critical = daysLeft <= 3;
  const totalDays = 30;
  const usedDays = Math.max(0, totalDays - daysLeft);
  const pct = Math.min(100, (usedDays / totalDays) * 100);
  const endDate = endAt
    ? new Date(endAt).toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
    : "";
  const accent = critical ? "#ef4444" : urgent ? "#f59e0b" : "var(--gold)";
  const glow = critical
    ? "rgba(239,68,68,0.15)"
    : urgent
    ? "rgba(245,158,11,0.12)"
    : "rgba(212,175,55,0.10)";

  return (
    <div
      className="relative overflow-hidden border rounded-none"
      style={{
        borderColor: critical ? "rgba(239,68,68,0.4)" : urgent ? "rgba(245,158,11,0.35)" : "rgba(212,175,55,0.35)",
        background: `linear-gradient(135deg, ${glow} 0%, rgba(10,10,10,0.6) 55%, rgba(0,0,0,0.9) 100%)`,
      }}
      data-testid="trial-countdown-hero"
    >
      {/* Decorative pulse ring */}
      <div
        className="absolute -top-16 -right-16 w-48 h-48 rounded-full opacity-30 blur-3xl"
        style={{ background: accent }}
      />
      <div className="relative p-4 sm:p-6 md:p-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
        <div className="shrink-0 flex items-center gap-3 sm:gap-4 min-w-0">
          <div
            className="shrink-0 h-12 w-12 sm:h-16 sm:w-16 flex items-center justify-center border-2 rounded-full"
            style={{ borderColor: accent, background: "rgba(0,0,0,0.5)" }}
          >
            {critical ? (
              <Clock size={22} style={{ color: accent }} className="animate-pulse" />
            ) : (
              <Gift size={22} style={{ color: accent }} />
            )}
          </div>
          <div className="min-w-0">
            <div
              className="text-[9px] sm:text-[10px] font-mono tracking-[0.25em] sm:tracking-[0.3em] uppercase"
              style={{ color: accent }}
            >
              30 Günlük Ücretsiz Deneme
            </div>
            <div className="font-display text-xl sm:text-2xl lg:text-3xl xl:text-4xl mt-1 leading-tight break-words">
              <span className="text-neutral-400 text-sm sm:text-base font-sans mr-1">Hoş geldiniz —</span>{" "}
              Deneme sürenizin bitmesine{" "}
              <span className="font-mono tabular-nums whitespace-nowrap" style={{ color: accent }} data-testid="trial-days-remaining">
                {daysLeft}
              </span>{" "}
              gün kaldı
            </div>
            {endDate && (
              <div className="text-[11px] sm:text-xs text-neutral-400 mt-1.5 break-words">
                Bitiş: <span className="text-neutral-200 font-medium">{endDate}</span>
                <span className="hidden sm:inline"> · Kredi kartı istenmez</span>
              </div>
            )}
          </div>
        </div>

        <div className="flex-1 min-w-0 hidden lg:block">
          {/* Progress track — desktop only */}
          <div className="relative h-2 bg-white/5 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 transition-all duration-700"
              style={{
                width: `${pct}%`,
                background: `linear-gradient(90deg, ${accent} 0%, ${accent}88 100%)`,
              }}
            />
          </div>
          <div className="flex justify-between text-[10px] font-mono tracking-wider text-neutral-500 mt-2">
            <span>1. GÜN</span>
            <span>{usedDays}. GÜN</span>
            <span>30. GÜN</span>
          </div>
        </div>

        {/* Mobile & tablet progress — compact bar */}
        <div className="lg:hidden">
          <div className="relative h-1.5 bg-white/5 overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 transition-all duration-700"
              style={{ width: `${pct}%`, background: accent }}
            />
          </div>
        </div>

        <Link
          to="/pricing"
          className="btn-primary-gold shrink-0 inline-flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 text-sm w-full lg:w-auto whitespace-nowrap"
          data-testid="trial-hero-cta"
        >
          <Sparkles size={14} />
          Hemen Abone Ol
          <ArrowRight size={14} />
        </Link>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { rates } = useRates();
  const { t } = useI18n();
  const { isTrial, trialDaysLeft, trialEndAt } = useSubscription();
  const [z, setZ] = useState(null);
  const [history, setHistory] = useState([]);
  const [txns, setTxns] = useState([]);
  const [zOpen, setZOpen] = useState(false);

  useEffect(() => {
    api.get("/reports/z").then((r) => setZ(r.data)).catch(() => {});
    api.get("/rates/history?metal=gold&limit=40").then((r) => {
      setHistory(r.data.map((d, i) => ({
        idx: i,
        sell: d.selling_price,
        t: new Date(d.timestamp).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second:"2-digit" }),
      })));
    }).catch(() => {});
    api.get("/transactions").then((r) => setTxns(r.data.slice(0, 6))).catch(() => {});

    // Anahtar Teslim Onboarding — dükkanın stoğu boşsa 26 standart ürünü
    // (Çeyrek, Yarım, Ata, Cumhuriyet, Burma Bilezik, Gram Altınlar, Dövizler)
    // Harem canlı fiyatı ile otomatik yükle. Backend idempotent — tekrar
    // tetiklense de duplicate oluşmaz. Yalnızca ilk yükte akan bir sinyal:
    api.get("/products?limit=1").then(async (r) => {
      const items = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      if (items.length === 0) {
        try {
          const seed = await api.post("/products/seed-defaults");
          const n = (seed.data?.created || []).length;
          if (n > 0) {
            toast.success(`🎁 Dükkanın hazırlandı — ${n} standart ürün canlı Harem fiyatıyla yüklendi.`, {
              description: "POS'ta 'Sık Kullanılanlar' şeridinden hemen test satışı yapabilirsin.",
              duration: 7000,
            });
          }
        } catch (_e) { /* sessiz — kullanıcı POS'tan manuel de yükleyebilir */ }
      }
    }).catch(() => {});

    const iv = setInterval(() => {
      api.get("/rates/history?metal=gold&limit=40").then((r) => {
        setHistory(r.data.map((d, i) => ({
          idx: i,
          sell: d.selling_price,
          t: new Date(d.timestamp).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second:"2-digit" }),
        })));
      }).catch(() => {});
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Genel Bakış · Bugün</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Panel</h1>
        </div>
        <button
          onClick={() => setZOpen(true)}
          data-testid="close-day-btn"
          className="btn-primary-gold flex items-center gap-2 group"
          title="Günün Z Raporunu görüntüle"
        >
          <Sun size={14} className="group-hover:rotate-45 transition-transform" />
          GÜNÜ KAPAT
        </button>
      </div>

      {isTrial && <TrialCountdownHero daysLeft={trialDaysLeft} endAt={trialEndAt} />}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <KPI label="Has Altın Satış" value={`${fmt4(rates.gold?.selling_price)} ₺`} subtext="Anlık kur" testid="kpi-gold" />
        <KPI label="Bugünkü Ciro" value={`${fmt2(z?.sale_total ?? 0)} ₺`} subtext={`${z?.transaction_count ?? 0} işlem`} testid="kpi-sale" />
        <KPI label="Nakit Tahsilat" value={`${fmt2(z?.cash_total ?? 0)} ₺`} testid="kpi-cash" />
        <KPI label="Net Has Bakiye" value={`${fmt4(z?.net_gold_gram ?? 0)} gr`} testid="kpi-gold-net" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8 card-flat p-6" data-testid="rate-chart-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="label-caps">Has Altın · Gram · Canlı Grafik</div>
              <div className="font-display text-2xl mt-2">Kur Hareketi</div>
            </div>
            <div className="font-mono text-2xl tabular-nums" style={{color:"var(--gold)"}}>
              {fmt4(rates.gold?.selling_price)} ₺
            </div>
          </div>
          <div className="mt-6 h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="t" tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#666", fontSize: 10 }} axisLine={false} tickLine={false} domain={["dataMin - 5", "dataMax + 5"]} tickFormatter={(v)=>v.toFixed(0)} />
                <Tooltip
                  contentStyle={{ background: "#111", border: "1px solid rgba(255,255,255,0.1)", fontSize: 12 }}
                  labelStyle={{ color: "#999" }}
                  formatter={(v) => [`${fmt4(v)} ₺`, "Satış"]}
                />
                <Line type="monotone" dataKey="sell" stroke="#D4AF37" strokeWidth={1.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="lg:col-span-4 card-flat p-6" data-testid="staff-card">
          <div className="label-caps">{t("dash.staff")}</div>
          <div className="font-display text-2xl mt-2 mb-4">{t("dash.today")}</div>
          {(z?.staff_performance || []).length === 0 && (
            <div className="text-sm text-neutral-500">{t("dash.no_txns")}</div>
          )}
          <div className="divide-y divide-white/5">
            {(z?.staff_performance || []).map((s) => (
              <div key={s.user_id} className="flex justify-between items-center py-3">
                <div>
                  <div className="text-sm">{s.name}</div>
                  <div className="label-caps mt-1">{s.count} işlem</div>
                </div>
                <div className="font-mono tabular-nums text-sm" style={{color:"var(--gold)"}}>{fmt2(s.total)} ₺</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="card-flat p-6" data-testid="recent-txn-card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="label-caps">{t("dash.recent")}</div>
            <div className="font-display text-2xl mt-1">{t("dash.ledger")}</div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-neutral-500 label-caps">
              <th className="text-left py-3">{t("common.date")}</th>
              <th className="text-left py-3">{t("table.type")}</th>
              <th className="text-left py-3">{t("table.payment")}</th>
              <th className="text-right py-3">{t("table.gram")}</th>
              <th className="text-right py-3">{t("common.amount")}</th>
            </tr>
          </thead>
          <tbody>
            {txns.length === 0 && (
              <tr><td colSpan={5} className="py-6 text-center text-neutral-500">{t("dash.no_txns_hint")}</td></tr>
            )}
            {txns.map((t2) => (
              <tr key={t2.id} className="border-t border-white/5">
                <td className="py-3 text-neutral-400">{new Date(t2.created_at).toLocaleString()}</td>
                <td className="py-3">{t2.type === "sale" ? t("type.sale") : t2.type === "scrap_buy" ? t("type.scrap") : t2.type}</td>
                <td className="py-3">{t2.payment_method === "cash" ? t("pay.cash") : t2.payment_method === "card" ? t("pay.card") : t("pay.transfer")}</td>
                <td className="py-3 text-right font-mono tabular-nums">{fmt4(t2.total_gold_gram)}</td>
                <td className="py-3 text-right font-mono tabular-nums" style={{color:"var(--gold)"}}>{fmt2(t2.total_amount_tl)} ₺</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ZReportModal open={zOpen} onClose={() => setZOpen(false)} />
    </div>
  );
}
